import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CompareArrows,
  Delete,
  Download,
  FindInPage,
  History,
  MoreVert,
  UploadFile,
} from '@mui/icons-material';

import { ILegalClientRelationship } from '../../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { ILegalTransaction } from '../../Interfaces/ILegalTransaction';
import { ILegalClientFullObject } from '../../Interfaces/ILegalClient';
import {
  ILegalClientDocumentFile,
  ILegalClientDocumentTypeEntry,
  ILegalClientDocumentVersion,
  formatFileSize,
  getCurrentVersion,
  getNextTempVersionId,
} from '../../Interfaces/ILegalClientDocumentVersion';
import { useDownloadDocument } from '../../hooks/reactQuerys/DocumentQueries';
import { DocumentVersionPreviewDialog } from './DocumentVersionPreviewDialog';
import { DocumentVersionUploadDialog } from './DocumentVersionUploadDialog';
import { DocumentVersionCompareDialog } from './DocumentVersionCompareDialog';
import { DocumentVersionHistoryDialog } from './DocumentVersionHistoryDialog';

interface IProps {
  legalClientId: number;
  relationships: ILegalClientRelationship[];
  legalTransactions: ILegalTransaction[];
  formValues: ILegalClientFullObject;
  setFormValues: React.Dispatch<React.SetStateAction<ILegalClientFullObject>>;
}

const formatDateTime = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
};

const formatDate = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleDateString('de-DE');
};

const userLabel = (version: ILegalClientDocumentVersion): string => {
  const name = [version.UserFirstName, version.UserLastName]
    .filter(part => part !== null && part !== undefined && part !== '')
    .join(' ');

  return name === '' ? 'System / Mandantenportal' : name;
};

/**
 * Dokumente eines Mandanten als Tabelle — je Dokumenttyp **eine** Zeile, die auf
 * die aktuellste Version verweist. Ältere Stände bleiben als Historie erhalten
 * und sind über den Versionsvergleich erreichbar.
 *
 * Eine Version kann aus mehreren Dateien bestehen (z. B. Vorder- und Rückseite
 * eines Ausweises) — die stehen in derselben Zeile. Welche Dokumenttypen zur
 * Verfügung stehen, hängt an der Mandantenart. Alles wird mit dem Speichern des
 * Mandanten übernommen.
 */
export const DocumentTab = ({ formValues, setFormValues }: IProps) => {
  const { downloadDocument, isPending: isDownloading } = useDownloadDocument();

  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [uploadTypeEntry, setUploadTypeEntry] =
    useState<ILegalClientDocumentTypeEntry | null>(null);
  const [compareTypeEntry, setCompareTypeEntry] =
    useState<ILegalClientDocumentTypeEntry | null>(null);
  /**
   * Dokumentart, deren Historie offen ist — als ID und nicht als Objekt: im
   * Dialog wird gelöscht, und die Liste muss danach den neuen Stand zeigen.
   * Ein festgehaltenes Objekt wäre in diesem Moment veraltet.
   */
  const [historyTypeId, setHistoryTypeId] = useState<number | null>(null);
  const [previewTypeEntry, setPreviewTypeEntry] =
    useState<ILegalClientDocumentTypeEntry | null>(null);
  const [previewIdDocument, setPreviewIdDocument] = useState<
    number | undefined
  >(undefined);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTypeEntry, setMenuTypeEntry] =
    useState<ILegalClientDocumentTypeEntry | null>(null);

  const typeEntryArray = formValues.DocumentTypeArray ?? [];

  /** Nur Typen mit Inhalt kommen in die Tabelle. */
  const rowArray = typeEntryArray.filter(
    entry => entry.VersionArray.length > 0
  );

  /** Für die Typ-Auswahl beim Hochladen: alles, was zur Mandantenart passt. */
  const selectableTypeArray = typeEntryArray.filter(
    entry => entry.isAvailableForLegalClientType !== false
  );

  /**
   * Vorschau der aktuellen Version öffnen — mit allen Dateien der Version,
   * nicht nur der ersten.
   */
  const handlePreview = (
    typeEntry: ILegalClientDocumentTypeEntry,
    idDocument?: number
  ) => {
    setPreviewIdDocument(idDocument);
    setPreviewTypeEntry(typeEntry);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    typeEntry: ILegalClientDocumentTypeEntry
  ) => {
    setMenuTypeEntry(typeEntry);
    setMenuAnchor(event.currentTarget);
  };

  /** Neue Version bzw. neues Dokument übernehmen — mit temporärer negativer ID. */
  const handleAddVersion = (
    idLegalClientDocumentType: number,
    fileArray: ILegalClientDocumentFile[],
    validUntil: string,
    note: string
  ) => {
    const targetEntry = typeEntryArray.find(
      entry => entry.idLegalClientDocumentType === idLegalClientDocumentType
    );

    if (targetEntry === undefined) {
      return;
    }

    const currentVersion = getCurrentVersion(targetEntry);
    const newVersion: ILegalClientDocumentVersion = {
      idLegalClientDocumentVersion: getNextTempVersionId(typeEntryArray),
      idLegalClientDocumentType: idLegalClientDocumentType,
      VersionNumber: (currentVersion?.VersionNumber ?? 0) + 1,
      Created_at: new Date().toISOString(),
      idUser: null,
      ValidUntil: validUntil === '' ? null : validUntil,
      Note: note === '' ? null : note,
      isCurrent: true,
      FileArray: fileArray,
    };

    setFormValues(previous => ({
      ...previous,
      DocumentTypeArray: (previous.DocumentTypeArray ?? []).map(entry =>
        entry.idLegalClientDocumentType === idLegalClientDocumentType
          ? {
              ...entry,
              VersionArray: [
                newVersion,
                ...entry.VersionArray.map(version => ({
                  ...version,
                  isCurrent: false,
                })),
              ],
            }
          : entry
      ),
    }));
  };

  /**
   * Eine bestimmte Version entfernen — auch eine ältere aus der Mitte, ohne die
   * jüngeren mitzunehmen. Das Backend behandelt `DocumentTypeArray` beim
   * Speichern als autoritativ und löscht, was nicht mehr darin steht.
   *
   * Die Versionsnummern der übrigen Stände bleiben stehen; sie werden nicht
   * nachgezogen, damit eine schon vergebene Nummer nicht plötzlich einen
   * anderen Stand bezeichnet. `isCurrent` wandert dagegen mit — der oberste
   * verbliebene Stand ist der aktuelle.
   */
  const handleRemoveVersion = (
    typeEntry: ILegalClientDocumentTypeEntry,
    version: ILegalClientDocumentVersion
  ) => {
    setFormValues(previous => ({
      ...previous,
      DocumentTypeArray: (previous.DocumentTypeArray ?? []).map(entry => {
        if (
          entry.idLegalClientDocumentType !==
          typeEntry.idLegalClientDocumentType
        ) {
          return entry;
        }

        const remaining = entry.VersionArray.filter(
          candidate =>
            candidate.idLegalClientDocumentVersion !==
            version.idLegalClientDocumentVersion
        );

        return {
          ...entry,
          VersionArray: remaining.map((candidate, index) => ({
            ...candidate,
            isCurrent: index === 0,
          })),
        };
      }),
    }));
  };

  /** Aktuelle Version eines Typs entfernen — die vorherige rückt nach. */
  const handleRemoveCurrentVersion = (
    typeEntry: ILegalClientDocumentTypeEntry
  ) => {
    const current = getCurrentVersion(typeEntry);

    if (current !== undefined) {
      handleRemoveVersion(typeEntry, current);
    }
  };

  const renderRow = (typeEntry: ILegalClientDocumentTypeEntry) => {
    const version = getCurrentVersion(typeEntry);

    if (version === undefined) {
      return null;
    }

    const firstFile = version.FileArray[0];
    const isUnsaved = (version.idLegalClientDocumentVersion ?? 0) < 0;

    return (
      <TableRow key={`type-${typeEntry.idLegalClientDocumentType}`} hover>
        <TableCell width={150}>
          {formatDateTime(version.Created_at)}
          <Typography variant='caption' color='text.secondary' display='block'>
            {userLabel(version)}
          </Typography>
        </TableCell>

        <TableCell>
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
          >
            <Typography variant='body2'>
              {typeEntry.LegalClientDocumentType}
            </Typography>
            {isUnsaved && (
              <Chip size='small' color='info' label='noch nicht gespeichert' />
            )}
            {typeEntry.isAvailableForLegalClientType === false && (
              <Chip
                size='small'
                color='warning'
                variant='outlined'
                label='nicht für diese Mandantenart'
              />
            )}
          </Stack>
        </TableCell>

        <TableCell width={280}>
          {version.FileArray.map(file => (
            <Typography
              key={`file-${file.idDocument}`}
              variant='body2'
              color={file.idDocument > 0 ? 'primary' : 'text.secondary'}
              sx={{ cursor: file.idDocument > 0 ? 'pointer' : 'default' }}
              onClick={() => handlePreview(typeEntry, file.idDocument)}
            >
              {file.Label !== null &&
              file.Label !== undefined &&
              file.Label !== ''
                ? `${file.Label}: ${file.FileName}`
                : file.FileName}
              {formatFileSize(file.FileSize) !== '' &&
                ` (${formatFileSize(file.FileSize)})`}
            </Typography>
          ))}
        </TableCell>

        <TableCell width={110}>
          Version {version.VersionNumber}
          {typeEntry.VersionArray.length > 1 && (
            <Typography
              variant='caption'
              color='text.secondary'
              display='block'
            >
              {typeEntry.VersionArray.length} Versionen
            </Typography>
          )}
        </TableCell>

        <TableCell width={120}>{formatDate(version.ValidUntil)}</TableCell>

        <TableCell width={110} align='right'>
          <Tooltip
            title={
              version.FileArray.length > 1
                ? `Vorschau (${version.FileArray.length} Dateien)`
                : 'Vorschau'
            }
          >
            <span>
              <IconButton
                size='small'
                disabled={firstFile === undefined || firstFile.idDocument < 0}
                onClick={() => handlePreview(typeEntry)}
              >
                <FindInPage fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Weitere Aktionen'>
            <IconButton
              size='small'
              onClick={(event: React.MouseEvent<HTMLElement>) =>
                handleOpenMenu(event, typeEntry)
              }
            >
              <MoreVert fontSize='small' />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const historyTypeEntry =
    typeEntryArray.find(
      entry => entry.idLegalClientDocumentType === historyTypeId
    ) ?? null;

  const menuVersion =
    menuTypeEntry === null ? undefined : getCurrentVersion(menuTypeEntry);
  const menuFirstFile = menuVersion?.FileArray[0];

  return (
    <Box>
      <DocumentVersionPreviewDialog
        isOpen={previewTypeEntry !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setPreviewTypeEntry(null);
            setPreviewIdDocument(undefined);
          }
        }}
        version={
          previewTypeEntry === null
            ? undefined
            : getCurrentVersion(previewTypeEntry)
        }
        typeName={previewTypeEntry?.LegalClientDocumentType ?? ''}
        initialIdDocument={previewIdDocument}
      />

      <DocumentVersionUploadDialog
        isOpen={isOpenUpload}
        setIsOpen={(isOpen: boolean) => {
          setIsOpenUpload(isOpen);

          if (!isOpen) {
            setUploadTypeEntry(null);
          }
        }}
        typeEntry={uploadTypeEntry}
        selectableTypeArray={
          uploadTypeEntry === null ? selectableTypeArray : undefined
        }
        onSave={handleAddVersion}
      />

      <DocumentVersionHistoryDialog
        isOpen={historyTypeEntry !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setHistoryTypeId(null);
          }
        }}
        typeEntry={historyTypeEntry}
        onDeleteVersion={handleRemoveVersion}
      />

      <DocumentVersionCompareDialog
        isOpen={compareTypeEntry !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setCompareTypeEntry(null);
          }
        }}
        typeEntry={compareTypeEntry}
      />

      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuList dense>
          <MenuItem
            disabled={
              isDownloading ||
              menuFirstFile === undefined ||
              menuFirstFile.idDocument < 0
            }
            onClick={() => {
              if (menuVersion !== undefined) {
                menuVersion.FileArray.filter(
                  file => file.idDocument > 0
                ).forEach(file => downloadDocument(file.idDocument));
              }
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Download fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {menuVersion !== undefined && menuVersion.FileArray.length > 1
                ? 'Alle Dateien herunterladen'
                : 'Herunterladen'}
            </ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              setUploadTypeEntry(menuTypeEntry);
              setIsOpenUpload(true);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <UploadFile fontSize='small' />
            </ListItemIcon>
            <ListItemText>Neue Version hochladen</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setHistoryTypeId(
                menuTypeEntry?.idLegalClientDocumentType ?? null
              );
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <History fontSize='small' />
            </ListItemIcon>
            <ListItemText>Ältere Versionen ansehen</ListItemText>
          </MenuItem>

          <MenuItem
            disabled={(menuTypeEntry?.VersionArray.length ?? 0) < 2}
            onClick={() => {
              setCompareTypeEntry(menuTypeEntry);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <CompareArrows fontSize='small' />
            </ListItemIcon>
            <ListItemText>Versionen vergleichen</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              if (menuTypeEntry !== null) {
                handleRemoveCurrentVersion(menuTypeEntry);
              }
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Delete fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {(menuTypeEntry?.VersionArray.length ?? 0) > 1
                ? 'Aktuelle Version löschen'
                : 'Dokument löschen'}
            </ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Button
          size='small'
          variant='outlined'
          startIcon={<UploadFile />}
          disabled={selectableTypeArray.length === 0}
          onClick={() => {
            setUploadTypeEntry(null);
            setIsOpenUpload(true);
          }}
        >
          Dokument hochladen
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant='caption' color='text.secondary'>
          {formValues.LegalClientType !== ''
            ? `Dokumenttypen für: ${formValues.LegalClientType}`
            : ''}
        </Typography>
      </Stack>

      {selectableTypeArray.length === 0 && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Für diese Mandantenart sind noch keine Dokumenttypen hinterlegt. Bei
          einem neu angelegten Mandanten stehen sie erst nach dem ersten
          Speichern zur Verfügung.
        </Alert>
      )}

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell width={150}>Hochgeladen</TableCell>
            <TableCell>Dokument</TableCell>
            <TableCell width={280}>Dateien</TableCell>
            <TableCell width={110}>Version</TableCell>
            <TableCell width={120}>Gültig bis</TableCell>
            <TableCell width={110} align='right'>
              Aktionen
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowArray.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant='body2' color='text.secondary'>
                  Es ist noch kein Dokument hinterlegt.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rowArray.map(typeEntry => renderRow(typeEntry))
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
